// =============================================================================
// domain/leakRules.ts — the ACTIVE-RULE chip's data, and nothing else.
// -----------------------------------------------------------------------------
// Beta's Day Overview opens with a banner naming the rule (or rules) the trader
// is currently testing — `renderActiveRuleChip()`. This is the READ half of
// that, and deliberately only the read.
//
// ── 🚨 THIS IS NOT THE LEAK LOOP ──
//
// Beta's "Spot it · Fix it · Prove it" loop is a whole subsystem: leak metrics,
// rule creation, baselines, a ledger, banked habits, and a Practice tab that
// grades replays. NONE of that is ported and none of it is needed here — the
// banner only ever displays `actives[].ruleText` and a count.
//
// ── 🆕 THE CONDITION ABOVE HAS NOW BEEN MET, AND THE FILE GREW ──
//
// This file used to say: "a half-ported loop that could CREATE a rule it cannot
// then measure would be worse than no loop at all." That was right, and the
// blocker was real — none of the leaks measured here.
//
// Six of the seven now do (`domain/leaks.ts`), so the lifecycle below is no
// longer half-ported: a rule can be frozen against a baseline and re-proved
// against the same rate. The caution is kept rather than deleted, because the
// SEVENTH leak still cannot be measured — `premature-exit` is declared
// unavailable, and a rule must never be offered against it.
//
// ── WHERE IT LIVES ──
//
// `preferences.appFlags.leakLoop`, which this client already reads whole and
// writes back whole — so the banner costs no new column, no new read, and
// carries no risk of dropping a field it does not understand (D62).
// =============================================================================

import type { LeakContext, LeakMetric, LeakTrade } from './leaks';
import { leakById } from './leaks';

export interface LeakRule {
  id: string;
  leakId: string;
  ruleText: string;
  /**
   * ⚠ ISO date the rule was written. Trades from here on are what Prove
   * measures, so this is not decoration — it is the window's lower bound.
   * Empty for a rule stored before baselines existed.
   */
  createdAt: string;
  /** Null when the rule was written without one — see `baselineUsable`. */
  baseline: LeakBaseline | null;
}

export interface LeakBaseline {
  value: number | null;
  n: number;
  /** The look-back the baseline came from, so Prove can state what it compared. */
  from: string | null;
  to: string;
  /**
   * Expectancy, frozen at the SAME moment from the SAME window.
   *
   * ⚠ Recomputing it later would compare the rule against a moving target, and
   * the trader could not tell an improvement from a drifting denominator.
   */
  guardrail: { value: number | null; n: number } | null;
}

const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function readBaseline(raw: unknown): LeakBaseline | null {
  if (!raw || typeof raw !== 'object') return null;
  const b = raw as Record<string, unknown>;
  const g = (b['guardrail'] && typeof b['guardrail'] === 'object')
    ? b['guardrail'] as Record<string, unknown>
    : null;
  return {
    value: numOrNull(b['value']),
    n: Number(b['n']) || 0,
    from: typeof b['from'] === 'string' ? b['from'] : null,
    to: typeof b['to'] === 'string' ? b['to'] : '',
    guardrail: g ? { value: numOrNull(g['value']), n: Number(g['n']) || 0 } : null,
  };
}

/**
 * The rules the trader is currently testing, oldest first.
 *
 * ── ⚠ TOLERATES THE PRE-MULTI SHAPE ──
 *
 * Beta migrated `{ active: <rule|null> }` to `{ actives: [] }` and its own
 * migration still folds the singular form in. A row written before that upgrade
 * — or by an older client that has not run the migration — still carries
 * `active`, and dropping it would silently hide a rule the trader believes is
 * running. Read-only, so this never writes the migrated shape back.
 *
 * ⚠ A rule with NO TEXT is dropped. The banner's entire content is that
 * sentence; an entry without one would render an empty italic gap and a count
 * that does not match what is on screen.
 */
export function activeLeakRules(appFlags: Record<string, unknown> | null | undefined): LeakRule[] {
  const loop = (appFlags ?? {})['leakLoop'];
  if (!loop || typeof loop !== 'object') return [];
  const l = loop as Record<string, unknown>;

  const raw: unknown[] = Array.isArray(l['actives'])
    ? (l['actives'] as unknown[])
    : (l['active'] && typeof l['active'] === 'object' ? [l['active']] : []);

  return raw.flatMap((x) => {
    if (!x || typeof x !== 'object') return [];
    const r = x as Record<string, unknown>;
    const text = typeof r['ruleText'] === 'string' ? r['ruleText'].trim() : '';
    if (!text) return [];
    return [{
      id: String(r['id'] ?? ''),
      leakId: String(r['leakId'] ?? ''),
      ruleText: text,
      createdAt: typeof r['createdAt'] === 'string' ? r['createdAt'] : '',
      baseline: readBaseline(r['baseline']),
    }];
  });
}

// =============================================================================
// THE LIFECYCLE — Fix it, and Prove it
// =============================================================================
//
// ── 🚨 1. ADHERENCE AND OUTCOME ARE JUDGED SEPARATELY. THAT IS THE POINT ──
//
// The most common false conclusion in any trading journal is "that rule didn't
// work" when the truth is "I never followed it." Splitting the two is the whole
// reason this feature exists, and every shortcut that merges them re-creates the
// bug it was built to kill.
//
// ⚠ AN UNMARKED TRADE IS "NO DATA", NEVER "DIDN'T FOLLOW". Inferring failure
// from silence would manufacture exactly that conclusion.
//
// ── 🚨 2. THE VERDICT LADDER IS ORDERED, AND EVERY RUNG IS LOAD-BEARING ──
//
//   no-baseline → too-soon → unmeasured → not-followed → guardrail → worked
//                                                                  ↘ didnt-work
//
//   * An unusable baseline can never reach an outcome branch.
//   * A rule you never followed produced no evidence either way, so it can never
//     read "didn't work".
//   * A rule that improved its own number while EXPECTANCY FELL reads
//     `guardrail`, never `worked`.
//
// ── 🚨 3. THE GUARDRAIL EXISTS BECAUSE EVERY LEAK METRIC IS GAMEABLE ──
//
// The clearest case is premature exit: it only ever counts the FAVOURABLE run
// after an exit, so a blanket "hold longer" rule mechanically shrinks it — even
// on a history where most exits were followed by a reversal. The metric would
// report a win while the account bled.
//
// ⚠ `degraded === null` — no expectancy baseline captured — REFUSES to certify
// rather than assuming it is fine. Unverifiable is not the same as safe.
// =============================================================================

/** Below this, the rule was never really tested. */
export const ADHERENCE_MIN_PCT = 70;
/** Marked trades needed before adherence can be judged at all. */
export const ADHERENCE_MIN_MARKED = 5;
/** |improvement| under this is no meaningful change — noise, not a result. */
export const FLAT_BAND_PCT = 10;
/** Expectancy may wobble this much before it counts as degraded. */
export const GUARDRAIL_TOLERANCE_PCT = 5;

export interface BaselineCheck { ok: boolean; reason: string }

/**
 * A baseline you cannot improve on — or one from too thin a sample — is not a
 * baseline.
 *
 * ⚠ REJECTING IT UP FRONT IS WHAT STOPS PROVE REPORTING A BOGUS REGRESSION. A
 * controllable-loss baseline already at $0/trade can only ever move the wrong
 * way, so a rule that held it at zero — a complete success — would be reported
 * as a failure.
 */
export function baselineUsable(
  leak: LeakMetric | null, baseline: LeakBaseline | null,
): BaselineCheck {
  if (!leak || !baseline || baseline.value === null) {
    return { ok: false, reason: 'No baseline could be measured for that window.' };
  }
  if (baseline.n < leak.minSample) {
    return {
      ok: false,
      reason: `Baseline window has only ${baseline.n} qualifying trades `
        + `(need ${leak.minSample}).`,
    };
  }
  if (leak.better === 'lower' && leak.floor !== undefined && baseline.value <= leak.floor) {
    return {
      ok: false,
      reason: `Baseline is already at the best attainable value (${leak.floor}) — `
        + 'there is nothing to improve.',
    };
  }
  if (leak.better === 'higher' && leak.ceiling !== undefined && baseline.value >= leak.ceiling) {
    return {
      ok: false,
      reason: `Baseline is already at the ceiling (${leak.ceiling}) — `
        + 'there is nothing to improve.',
    };
  }
  return { ok: true, reason: '' };
}

// ---------------------------------------------------------------------------
// Adherence
// ---------------------------------------------------------------------------

/**
 * A trade carrying its per-rule marks. Rides `extras.ruleAdherences`.
 *
 * ⚠ `ruleAdherence` — SINGULAR — is the pre-multi shape: one mark for one rule,
 * from before several rules could run at once. The old client still reads it and
 * folds it in on the next write, so this client has to as well. Ignoring it
 * would show a trade the trader had already judged as unmarked, and the first
 * mark written over it would delete their answer.
 */
export interface AdherenceTrade {
  ruleAdherences?: Record<string, unknown> | null;
  ruleAdherence?: { ruleId?: unknown; followed?: unknown } | null;
}

const boolOrNull = (v: unknown): boolean | null =>
  (v === true ? true : v === false ? false : null);

/**
 * Did the trader hold this rule on this trade?
 *
 * ⚠ THREE OUTCOMES, and `null` — unmarked — is not `false`. Anything stored
 * that is neither `true` nor `false` also reads as unmarked rather than being
 * coerced: a half-written mark is not a judgement.
 *
 * ⚠ PER RULE. Several rules run at once, and one mark must never clobber
 * another — the same reason a trade pins its pattern version.
 */
export function adherenceMark(t: AdherenceTrade, ruleId: string): boolean | null {
  if (!t || !ruleId) return null;
  const m = t.ruleAdherences;
  if (m && typeof m === 'object'
      && Object.prototype.hasOwnProperty.call(m, ruleId)) {
    return boolOrNull(m[ruleId]);
  }
  // ⚠ The legacy single mark, and only when it names THIS rule. A mark left over
  // from a different rule is not an answer about this one.
  const legacy = t.ruleAdherence;
  if (legacy && typeof legacy === 'object' && legacy.ruleId === ruleId) {
    return boolOrNull(legacy.followed);
  }
  return null;
}

/**
 * The marks a trade should carry after judging one rule.
 *
 * Returns the whole `ruleAdherences` map — the caller applies it to a HYDRATED
 * trade and drops the legacy key. Pure, so the toggle logic is testable without
 * a database.
 *
 * ⚠ `null` DELETES THE KEY rather than storing false. "I have not judged this"
 * and "I broke the rule" are different facts, and adherence is judged only on
 * trades that were actually marked — a stored `false` counts against the trader
 * in the verdict, so an un-mark that left one behind would keep punishing them.
 *
 * ⚠ THE LEGACY SINGLE MARK IS FOLDED IN FIRST, even when it names a DIFFERENT
 * rule. It is about to be deleted; not carrying it across would throw away a
 * judgement the trader made about some other rule entirely.
 */
export function marksAfterSetting(
  t: AdherenceTrade, ruleId: string, followed: boolean | null,
): Record<string, unknown> {
  const stored = (t?.ruleAdherences && typeof t.ruleAdherences === 'object')
    ? { ...t.ruleAdherences }
    : {};

  if (!t?.ruleAdherences || typeof t.ruleAdherences !== 'object') {
    const legacy = t?.ruleAdherence;
    if (legacy && typeof legacy === 'object' && typeof legacy.ruleId === 'string'
        && legacy.ruleId) {
      stored[legacy.ruleId] = legacy.followed === true;
    }
  }

  if (followed === null) delete stored[ruleId];
  else stored[ruleId] = followed === true;
  return stored;
}

/**
 * What a click on one of the three segments means.
 *
 * ⚠ A TOGGLE, matching the old client: pressing the state a trade is already in
 * CLEARS it, and "—" always clears. Without that there is no way back to
 * unmarked, and unmarked is the state that keeps a trade out of the adherence
 * denominator entirely.
 */
export function nextMark(
  current: boolean | null, pressed: 'true' | 'false' | 'skip',
): boolean | null {
  if (pressed === 'skip') return null;
  if (pressed === 'true') return current === true ? null : true;
  return current === false ? null : false;
}

export interface Adherence { marked: number; followed: number; pct: number | null }

export function ruleAdherence(
  ruleId: string, trades: readonly AdherenceTrade[],
): Adherence {
  let marked = 0; let followed = 0;
  for (const t of trades) {
    const v = adherenceMark(t, ruleId);
    // ⚠ SKIPPED, not counted as a failure — see the header.
    if (v === null) continue;
    marked += 1;
    if (v === true) followed += 1;
  }
  return { marked, followed, pct: marked ? (followed / marked) * 100 : null };
}

// ---------------------------------------------------------------------------
// The guardrail
// ---------------------------------------------------------------------------

export interface GuardrailDelta { deltaPct: number | null; degraded: boolean | null }

/**
 * Expectancy, compared higher-is-better.
 *
 * ⚠ DIVIDING BY `|baseline|` KEEPS THE SIGN HONEST ON A LOSING BASELINE.
 * −$10/trade improving to −$5 is a 50% improvement; dividing by the signed value
 * reports −50% and the guardrail would block a rule that helped.
 */
export function guardrailDelta(
  baseline: number | null, current: number | null,
): GuardrailDelta {
  if (baseline === null || current === null) return { deltaPct: null, degraded: null };
  const raw = current - baseline;
  if (Math.abs(baseline) > 0) {
    const pct = (raw / Math.abs(baseline)) * 100;
    return { deltaPct: pct, degraded: pct < -GUARDRAIL_TOLERANCE_PCT };
  }
  // ⚠ No denominator → direction only. A percentage against a zero baseline
  // would be fabricated.
  return { deltaPct: null, degraded: raw < 0 };
}

// ---------------------------------------------------------------------------
// Prove
// ---------------------------------------------------------------------------

export type Verdict =
  | 'no-baseline' | 'too-soon' | 'unmeasured' | 'not-followed'
  | 'guardrail' | 'worked' | 'didnt-work';

export interface ProveResult {
  ruleId: string;
  leakId: string;
  label: string;
  unit: LeakMetric['unit'];
  baseline: number | null;
  baselineN: number | null;
  current: number | null;
  n: number;
  delta: number | null;
  deltaPct: number | null;
  improved: boolean | null;
  adherence: Adherence;
  guardrail: {
    baseline: number | null; current: number | null;
    deltaPct: number | null; degraded: boolean | null;
  };
  verdict: Verdict;
  reason: string;
}

const money = (v: number | null): string => (v === null ? '—' : `$${v.toFixed(2)}`);

/**
 * The verdict, and every number behind it.
 *
 * @param since trades taken since the rule, windowed by the CALLER — the window
 *   is a population question and belongs at the seam, not in here.
 * @param currentExpectancy expectancy per trade over that same window.
 *
 * ⚠ THE GUARDRAIL IS ALWAYS MEASURED AND REPORTED, even when the ladder stops
 * before it. A falling expectancy is worth seeing beside a "not followed" too —
 * it is the trader's own P&L, and it does not stop mattering because the rule
 * went untested.
 */
export function proveRule(
  rule: LeakRule,
  since: readonly (LeakTrade & AdherenceTrade)[],
  ctx: LeakContext,
  currentExpectancy: number | null,
): ProveResult | null {
  const leak = leakById(rule?.leakId ?? '');
  if (!rule || !leak) return null;

  const current = leak.measure(since, ctx);
  const adherence = ruleAdherence(rule.id, since);
  const baseVal = rule.baseline?.value ?? null;
  const usable = baselineUsable(leak, rule.baseline);

  const gBase = rule.baseline?.guardrail?.value ?? null;
  const gd = guardrailDelta(gBase, currentExpectancy);

  const out: ProveResult = {
    ruleId: rule.id, leakId: leak.id, label: leak.label, unit: leak.unit,
    baseline: baseVal, baselineN: rule.baseline?.n ?? null,
    current: current.value, n: current.n,
    delta: null, deltaPct: null, improved: null,
    adherence,
    guardrail: {
      baseline: gBase, current: currentExpectancy,
      deltaPct: gd.deltaPct, degraded: gd.degraded,
    },
    verdict: 'no-baseline', reason: '',
  };

  if (baseVal !== null && current.value !== null) {
    const raw = current.value - baseVal;
    // ⚠ `better` decides the SIGN of an improvement. Capture efficiency rising
    // is good; a tilt gap rising is bad. One comparison cannot serve both.
    const good = leak.better === 'lower' ? -raw : raw;
    out.delta = raw;
    if (Math.abs(baseVal) > 0) {
      out.deltaPct = (good / Math.abs(baseVal)) * 100;
      // ⚠ A FLAT BAND. Without it a 0.4% wobble reads as an improvement and
      // every rule gets credited for noise.
      out.improved = out.deltaPct >= FLAT_BAND_PCT;
    } else {
      out.deltaPct = null;
      out.improved = good > 0;
    }
  }

  if (!usable.ok) {
    out.verdict = 'no-baseline';
    out.reason = usable.reason;
    return out;
  }
  if (current.value === null || current.n < leak.minSample) {
    out.verdict = 'too-soon';
    out.reason = `Need ${leak.minSample} qualifying trades since the rule — `
      + `you have ${current.n}.`;
    return out;
  }
  if (adherence.pct === null || adherence.marked < ADHERENCE_MIN_MARKED) {
    out.verdict = 'unmeasured';
    out.reason = `Only ${adherence.marked} trade${adherence.marked === 1 ? '' : 's'} `
      + 'marked for this rule — adherence cannot be judged yet.';
    return out;
  }
  if (adherence.pct < ADHERENCE_MIN_PCT) {
    out.verdict = 'not-followed';
    out.reason = `You held the rule on ${Math.round(adherence.pct)}% of marked `
      + 'trades. It was never really tested.';
    return out;
  }
  if (out.improved) {
    // ⚠ `degraded !== false`, so BOTH "expectancy fell" and "we could not tell"
    // block the credit. Unverifiable is not the same as safe.
    if (out.guardrail.degraded !== false) {
      out.verdict = 'guardrail';
      out.reason = out.guardrail.degraded
        ? `${leak.label} improved, but expectancy fell from `
          + `${money(out.guardrail.baseline)} to ${money(out.guardrail.current)} `
          + 'per trade. The rule is improving its own number at the cost of P&L.'
        : `${leak.label} improved, but no expectancy baseline was captured — `
          + 'the rule cannot be certified.';
      return out;
    }
    out.verdict = 'worked';
    const move = out.deltaPct !== null
      ? `${Math.round(Math.abs(out.deltaPct))}% the right way`
      : 'the right way';
    out.reason = `Held on ${Math.round(adherence.pct)}% of trades and `
      + `${leak.label} moved ${move}.`;
    return out;
  }
  out.verdict = 'didnt-work';
  out.reason = `You held the rule (${Math.round(adherence.pct)}%) but `
    + `${leak.label} did not move. Sharpen it, or pick a different leak.`;
  return out;
}
